/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type AnimationEvent,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { cssMap, jsx, keyframes } from '@compiled/react';

import { useIsReducedMotion } from '@atlaskit/motion/accessibility';
import ExitingPersistence, { useExitingPersistence } from '@atlaskit/motion/exiting-persistence';
import { useResizing } from '@atlaskit/motion/resizing';
import { token } from '@atlaskit/tokens';

import { RovoThinkingBar, type RovoThinkingBarProps } from './RovoThinkingBar';
import { RovoThinkingError, type RovoThinkingErrorProps } from './RovoThinkingError';
import { RovoThinkingReady, type RovoThinkingReadyProps } from './RovoThinkingReady';

const fadeIn = keyframes({
	from: { opacity: 0 },
	to: { opacity: 1 },
});

const fadeOut = keyframes({
	from: { opacity: 1 },
	to: { opacity: 0 },
});

const styles = cssMap({
	entering: {
		animationName: fadeIn,
		animationDuration: token('motion.duration.long'),
		animationTimingFunction: token('motion.easing.inout.bold'),
		animationFillMode: 'forwards',
	},
	exiting: {
		animationName: fadeOut,
		animationDuration: token('motion.duration.long'),
		animationTimingFunction: token('motion.easing.inout.bold'),
		animationFillMode: 'forwards',
		pointerEvents: 'none',
	},
	reducedMotion: {
		'@media (prefers-reduced-motion: reduce)': {
			animationDuration: '0ms',
		},
	},
	resizingContainer: {
		display: 'grid',
	},
	intrinsicReadyWidth: {
		width: 'max-content',
		minWidth: '100%',
	},
	overlappingItem: {
		gridColumn: 1,
		gridRow: 1,
	},
	invisible: {
		visibility: 'hidden',
		whiteSpace: 'nowrap',
	},
	hidden: {
		display: 'none',
	},
});

type FadeProps = {
	children: ReactNode;
};

/**
 * Inner child for `<ExitingPersistence>`. Reads `isExiting` / `onFinish`
 * from context so the parent can sequence exit-then-enter, and applies our
 * own fade keyframes.
 */
function Fade({ children }: FadeProps): JSX.Element {
	const { isExiting, onFinish } = useExitingPersistence();

	return (
		<div
			css={[styles.reducedMotion, isExiting ? styles.exiting : styles.entering]}
			onAnimationEnd={(event: AnimationEvent<HTMLDivElement>) => {
				if (event.target !== event.currentTarget) {
					return;
				}
				onFinish?.();
			}}
		>
			{children}
		</div>
	);
}

/**
 * Props passed through to `RovoThinkingBar`. `isComplete`is managed internally by the transition
 * and should not be set by the consumer.
 */

export type RovoThinkingReadyTransitionProps = {
	/**
	 * When `true`, the transition kicks off: the thinking bar's progress
	 * animates to 100%, then cross-fades into the `RovoThinkingReady` state.
	 */
	isReady: boolean;
	/**
	 * When `true`, `thinkingReadyProps.onClose` is called automatically 10 seconds
	 * after the ready state becomes visible. The timer is cancelled if `isReady`
	 * flips back to `false` before it fires, or when the component unmounts.
	 * Defaults to `false`.
	 */
	autoClose?: boolean;
	/**
	 * The delay in milliseconds after the ready state becomes visible before the auto-close timer fires.
	 * Defaults to 10 seconds.
	 */
	autoCloseDelay?: number;
	resizeToReadyContent?: boolean;
	/**
	 * Props forwarded to `RovoThinkingBar` (displayed while `isReady` is `false`).
	 * `isComplete` is controlled internally.
	 */
	thinkingBarProps: Omit<RovoThinkingBarProps, 'isComplete'>;
	/**
	 * Props forwarded to `RovoThinkingReady` (displayed once the transition completes).
	 */
	thinkingReadyProps: RovoThinkingReadyProps;
	/**
	 * When `true`, the thinking bar is replaced immediately with the
	 * `RovoThinkingError` screen (no progress-to-100% animation). Takes priority
	 * over `isReady`. Requires `thinkingErrorProps` to be provided.
	 */
	isError?: boolean;
	/**
	 * Props forwarded to `RovoThinkingError` (displayed while `isError` is `true`).
	 */
	thinkingErrorProps?: RovoThinkingErrorProps;
};

type ResizePhase = 'thinking' | 'resizing' | 'exiting' | 'ready';

/**
 * Orchestrates the full thinking → ready lifecycle:
 *
 * 1. Renders `RovoThinkingBar` (wrapped in `RovoThinkingBarWrapper`) while
 *    `isReady` is `false`.
 * 2. When `isReady` flips to `true`, sets `isComplete` on `RovoThinkingBar`
 *    so its progress bar animates to 100% and
 *    calls `onCompleteAnimationFinished` in the thinking bar.
 *
 * Usage:
 * ```tsx
 * <RovoThinkingReadyTransition
 *   isReady={isReady}
 *   thinkingBarProps={{ status: 'analyzing', onStop }}
 *   thinkingReadyProps={{ onView, onClose }}
 * />
 * ```
 */
const READY_DELAY_MS = 400;
const MIN_REMAINING_MS = 5_000;

export function RovoThinkingReadyTransition({
	isReady,
	autoClose = false,
	autoCloseDelay = 10_000,
	resizeToReadyContent = false,
	thinkingBarProps,
	thinkingReadyProps,
	isError = false,
	thinkingErrorProps,
}: RovoThinkingReadyTransitionProps): JSX.Element {
	// `showReady` flips only after the progress bar animation finishes AND the
	// 800 ms hold delay elapses, driving the ExitingPersistence cross-fade.
	const [showReady, setShowReady] = useState(false);
	const [resizePhase, setResizePhase] = useState<ResizePhase>('thinking');
	const prefersReducedMotion = useIsReducedMotion();

	const handleResizeFinished = useCallback(() => {
		setResizePhase((currentPhase) => (currentPhase === 'resizing' ? 'exiting' : currentPhase));
	}, []);

	const resizing = useResizing({
		dimension: 'width',
		duration: token('motion.duration.short'),
		easing: token('motion.easing.out.practical'),
		onFinishMotion: handleResizeFinished,
	});

	// Incremented every time we reset to the thinking state. Used as the key on
	// ExitingPersistence so that any in-flight CSS transition is immediately
	// discarded on reset — preventing the component from getting stuck mid-fade
	// when isReady is toggled rapidly while a transition is running.
	const [sessionKey, setSessionKey] = useState(0);

	// Keep a stable ref to onClose so the setTimeout callback always calls the
	// latest version without needing it in the effect dependency array.
	const onCloseRef = useRef(thinkingReadyProps.onClose);
	useEffect(() => {
		onCloseRef.current = thinkingReadyProps.onClose;
	}, [thinkingReadyProps.onClose]);

	// Tracks whether autoClose has already fired for the current ready session.
	// Reset to false whenever isReady goes false so a subsequent ready session
	// gets its own independent auto-close.
	const autoCloseFiredRef = useRef(false);

	// Stable ref to the pending ready-delay timer so the reset effect can
	// cancel it without needing it in the dependency array.
	const readyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// --- Hover-aware auto-close timer ---
	//
	// `isHovered` pauses the countdown while the pointer is over the component.
	// `remainingMs` carries the un-consumed budget across hover sessions:
	//   - null  → use the full autoCloseDelay
	//   - number → resume from this many ms
	//
	// On mouse-enter we snapshot how much time was left when the hover began.
	// On mouse-leave we compute elapsed hover duration, subtract it from the
	// snapshot, and clamp to min(MIN_REMAINING_MS, autoCloseDelay) so the user always gets
	// at least 5 s (or the full configured duration if shorter) after lifting
	// the mouse. Setting `remainingMs` re-triggers the effect which starts a
	// fresh setTimeout for exactly that duration.
	const [isHovered, setIsHovered] = useState(false);
	const [remainingMs, setRemainingMs] = useState<number | null>(null);

	// When the current countdown started (wall-clock). Used to compute elapsed
	// time when the mouse enters so we know the remaining budget.
	const timerStartRef = useRef<number | null>(null);
	// Wall-clock timestamp when the mouse entered.
	const hoverStartRef = useRef<number | null>(null);
	// Remaining budget at the moment the mouse entered.
	const remainingAtHoverRef = useRef<number | null>(null);

	// Reset back to the thinking state whenever `isReady` goes false again
	// (e.g. a new request starts after a previous one completed).
	// Also cancel any in-flight ready-delay timer and reset all auto-close state.
	useEffect(() => {
		if (!isReady) {
			if (readyTimerRef.current !== null) {
				clearTimeout(readyTimerRef.current);
				readyTimerRef.current = null;
			}
			autoCloseFiredRef.current = false;
			timerStartRef.current = null;
			hoverStartRef.current = null;
			remainingAtHoverRef.current = null;
			setRemainingMs(null);
			setIsHovered(false);
			setShowReady(false);
			setResizePhase('thinking');
			setSessionKey((k) => k + 1);
		}
	}, [isReady]);

	// Auto-close effect: starts a countdown of `remainingMs ?? autoCloseDelay`
	// whenever autoClose is enabled, the ready state is visible, the user is not
	// hovering, and auto-close hasn't already fired this session.
	// Records `timerStartRef` so mouse-enter can compute elapsed time.
	useEffect(() => {
		if (!autoClose || !showReady || isHovered || autoCloseFiredRef.current) {
			return;
		}
		const delay = remainingMs ?? autoCloseDelay;
		timerStartRef.current = Date.now();
		const timerId = setTimeout(() => {
			autoCloseFiredRef.current = true;
			onCloseRef.current();
		}, delay);
		return () => {
			clearTimeout(timerId);
			timerStartRef.current = null;
		};
	}, [autoClose, showReady, isHovered, remainingMs, autoCloseDelay]);

	const handlePauseTimer = useCallback(() => {
		if (!autoClose || autoCloseFiredRef.current) {
			return;
		}
		// Snapshot the remaining budget at the moment of hover.
		const elapsed = timerStartRef.current !== null ? Date.now() - timerStartRef.current : 0;
		remainingAtHoverRef.current = Math.max(0, (remainingMs ?? autoCloseDelay) - elapsed);
		hoverStartRef.current = Date.now();
		setIsHovered(true);
	}, [autoClose, autoCloseDelay, remainingMs]);

	const handleResumeTimer = useCallback(() => {
		if (!autoClose || autoCloseFiredRef.current) {
			return;
		}
		const hoverDuration = hoverStartRef.current !== null ? Date.now() - hoverStartRef.current : 0;
		hoverStartRef.current = null;

		const budgetBeforeHover = remainingAtHoverRef.current ?? autoCloseDelay;
		remainingAtHoverRef.current = null;

		// Subtract the time spent hovering; clamp to the minimum floor.
		const afterHover = budgetBeforeHover - hoverDuration;
		const minFloor = Math.min(MIN_REMAINING_MS, autoCloseDelay);
		setRemainingMs(Math.max(afterHover, minFloor));
		setIsHovered(false);
	}, [autoClose, autoCloseDelay]);

	const handleCompleteAnimationFinished = useCallback(() => {
		// Wait 800 ms after the progress bar reaches 100% before showing the
		// ready state, giving the completion moment a brief moment to breathe.
		readyTimerRef.current = setTimeout(() => {
			readyTimerRef.current = null;
			if (resizeToReadyContent) {
				if (prefersReducedMotion) {
					setResizePhase('ready');
					setShowReady(true);
				} else {
					setResizePhase('resizing');
				}
			} else {
				setShowReady(true);
			}
		}, READY_DELAY_MS);
		thinkingBarProps.onCompleteAnimationFinished?.();
	}, [prefersReducedMotion, resizeToReadyContent, thinkingBarProps]);

	return (
		<div
			data-testid="rovo-thinking-ready-transition"
			onMouseEnter={autoClose ? handlePauseTimer : undefined}
			onMouseLeave={autoClose ? handleResumeTimer : undefined}
			onFocus={autoClose ? handlePauseTimer : undefined}
			onBlur={autoClose ? handleResumeTimer : undefined}
			onFocusCapture={autoClose ? handlePauseTimer : undefined}
			onBlurCapture={autoClose ? handleResumeTimer : undefined}
		>
			{isError && thinkingErrorProps ? (
				// Failure short-circuits the thinking → ready animation (both the
				// resize and non-resize paths): cross-fade straight to the error
				// screen regardless of progress state.
				<ExitingPersistence key={sessionKey} exitThenEnter>
					<Fade key="error">
						<RovoThinkingError {...thinkingErrorProps} />
					</Fade>
				</ExitingPersistence>
			) : resizeToReadyContent ? (
				<div
					ref={resizing.ref}
					css={[styles.resizingContainer, resizePhase !== 'thinking' && styles.intrinsicReadyWidth]}
				>
					{resizePhase !== 'thinking' && (
						<div
							aria-hidden={resizePhase !== 'ready'}
							css={[
								styles.overlappingItem,
								resizePhase !== 'ready' && styles.invisible,
								resizePhase === 'ready' && styles.entering,
								styles.reducedMotion,
							]}
						>
							<RovoThinkingReady {...thinkingReadyProps} />
						</div>
					)}
					<div
						css={[
							styles.overlappingItem,
							resizePhase === 'thinking' && styles.entering,
							resizePhase === 'exiting' && styles.exiting,
							resizePhase === 'ready' && styles.hidden,
							styles.reducedMotion,
						]}
						onAnimationEnd={(event: AnimationEvent<HTMLDivElement>) => {
							if (event.target === event.currentTarget && resizePhase === 'exiting') {
								setResizePhase('ready');
								setShowReady(true);
							}
						}}
					>
						<RovoThinkingBar
							{...thinkingBarProps}
							isComplete={isReady}
							onCompleteAnimationFinished={handleCompleteAnimationFinished}
						/>
					</div>
				</div>
			) : (
				<ExitingPersistence key={sessionKey} exitThenEnter>
					{!showReady && (
						<Fade key="thinking">
							<RovoThinkingBar
								{...thinkingBarProps}
								isComplete={isReady}
								onCompleteAnimationFinished={handleCompleteAnimationFinished}
							/>
						</Fade>
					)}
					{showReady && (
						<Fade key="ready">
							<RovoThinkingReady {...thinkingReadyProps} />
						</Fade>
					)}
				</ExitingPersistence>
			)}
		</div>
	);
}
